import { Component, Inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { CoreTargetService } from '../../../../core/services/target.service';

@Component({
  selector: 'app-start-client-integration',
  standalone: true,
  imports: [CommonModule, MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="recorder_modal integration-modal">
      <h2 mat-dialog-title class="recorder_modal_title">
        Start client integration
      </h2>
      <mat-dialog-content>
        <p class="integration-subtitle">
          How would you like to create experiments for
          <strong>{{ data.name }}</strong
          >?
        </p>
        <div class="integration-options">
          <div class="integration-option">
            <mat-icon class="integration-icon" aria-hidden="true"
              >collections_bookmark</mat-icon
            >
            <div class="integration-copy">
              <h3>Choose from the library</h3>
              <p>Browse nudges and adapt your selection for this client.</p>
            </div>
            <button
              mat-raised-button
              type="button"
              class="primary"
              (click)="open('library')"
              [disabled]="opening"
            >
              Nudge Library
            </button>
          </div>
          <div class="integration-option">
            <mat-icon class="integration-icon" aria-hidden="true"
              >auto_awesome</mat-icon
            >
            <div class="integration-copy">
              <h3>Build for a page</h3>
              <p>Choose a page type to start building experiments.</p>
            </div>
            <button
              mat-raised-button
              type="button"
              class="primary"
              (click)="open('builder')"
              [disabled]="opening"
            >
              Nudge Builder
            </button>
          </div>
        </div>
        <p *ngIf="error" class="integration-error" role="alert">{{ error }}</p>
      </mat-dialog-content>
      <div class="button_container">
        <button
          mat-button
          type="button"
          class="seo_cancel"
          mat-dialog-close
          [disabled]="opening"
        >
          Cancel
        </button>
      </div>
    </div>
  `,
  styles: [
    `
      :host {
        display: block;
        color: var(--text-main-color);
      }
      .integration-modal .recorder_modal_title {
        margin: 0 0 12px;
        padding: 0;
        font-size: 18px;
        font-weight: 500;
        line-height: 1.4;
        color: var(--text-main-color);
      }
      .recorder_modal_title::before {
        display: none;
      }
      mat-dialog-content {
        padding: 0;
        color: var(--subtitle-main-color);
      }
      .integration-subtitle {
        margin: 0 0 24px;
        font-size: 14px;
        line-height: 1.6;
        overflow-wrap: anywhere;
      }
      .integration-subtitle strong {
        color: var(--text-main-color);
        font-weight: 500;
      }
      .integration-options {
        display: grid;
        gap: 12px;
      }
      .integration-option {
        display: flex;
        align-items: center;
        gap: 12px;
        padding: 16px;
        background: var(--sidenav-theme-color);
        border: 1px solid var(--border-color-theme);
        border-radius: 10px;
      }
      .integration-modal .integration-icon {
        flex-shrink: 0;
        color: var(--secondary-theme-color) !important;
        width: 22px;
        height: 22px;
        font-size: 22px;
      }
      .integration-copy {
        flex: 1;
        min-width: 0;
      }
      .integration-copy h3 {
        margin: 0 0 4px;
        font-size: 14px;
        line-height: 1.4;
        font-weight: 500;
        color: var(--text-main-color);
      }
      .integration-copy p {
        margin: 0;
        font-size: 12px;
        line-height: 1.5;
        color: var(--subtitle-main-color);
        opacity: 0.8;
      }
      .integration-modal button.primary {
        flex-shrink: 0;
        min-width: 112px;
        height: 36px;
        border-radius: 10px;
        background-color: var(--secondary-theme-color) !important;
        border-color: var(--secondary-theme-color) !important;
      }
      .integration-modal button.primary:hover:not(:disabled) {
        filter: brightness(0.96);
      }
      .integration-modal button:focus-visible {
        outline: 2px solid var(--secondary-theme-color);
        outline-offset: 3px;
      }
      .integration-modal .button_container {
        margin-top: 24px;
      }
      .integration-modal .seo_cancel {
        color: var(--text-main-color) !important;
        height: 36px;
      }
      .integration-error {
        margin: 16px 0 0;
        font-size: 13px;
        line-height: 1.5;
        color: var(--text-main-color);
      }
      @media (max-width: 520px) {
        .integration-option {
          flex-wrap: wrap;
        }
        .integration-copy {
          flex-basis: calc(100% - 34px);
        }
        .integration-modal button.primary {
          width: 100%;
        }
      }
    `,
  ],
})
export class StartClientIntegrationComponent {
  opening = false;
  error = '';

  constructor(
    @Inject(MAT_DIALOG_DATA)
    public readonly data: { target: string; name: string },
    private readonly dialogRef: MatDialogRef<StartClientIntegrationComponent>,
    private readonly router: Router,
    private readonly targetService: CoreTargetService,
  ) {}

  async open(choice: 'library' | 'builder'): Promise<void> {
    if (this.opening) return;
    if (this.targetService.getSelectedTarget('target') !== this.data.target) {
      this.error = `Select ${this.data.name} in the client selector to start integration.`;
      return;
    }
    this.opening = true;
    this.error = '';
    try {
      const navigated =
        choice === 'library'
          ? await this.router.navigate(['/master-preview'], {
              queryParams: { tab: 'library' },
            })
          : await this.router.navigate(['/nudge-builder/explore']);
      if (navigated) this.dialogRef.close();
      else
        this.error =
          'Unable to open this page. Please check your access and try again.';
    } catch {
      this.error = 'Unable to open this page. Please try again.';
    } finally {
      this.opening = false;
    }
  }
}
